// components/CardPagos.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  TablePagination,
  TableFooter
} from '@mui/material';
import {
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import moment from 'moment';
import CardExpandible from '../CardExpandible';
import { getCtaCte, getResumenPagosSucursal } from '../../services/pagos.service';
import { formatNumber, getMedioPagoIcon, getMedioPagoColor } from './mediosPagoHelpers';
import DialogFacturasMedioPago from './DialogFacturasMedioPago';

const CardPagos = ({ 
  sucursal,
  desde, 
  hasta, 
  loading = false,
  triggerRecarga = 0
}) => {
  // Estados
  const [data, setData] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});
  const [ctaCte, setCtaCte] = useState(null);
  const [dialogFacturasOpen, setDialogFacturasOpen] = useState(false);
  const [medioPagoSeleccionado, setMedioPagoSeleccionado] = useState(null);
  // Extraer datos de sucursal
  const sucursalId = sucursal?.id;
  const sucursalNombre = sucursal?.nombre;
  const mpStoreId = sucursal?.mp_store_id;

  // Efecto para cargar datos
  useEffect(() => {
    if (sucursalId && desde && hasta) {
      cargarDatos();
    }
  }, [sucursalId, desde, hasta, triggerRecarga]);

  const cargarDatos = async () => {
  setLoadingData(true);
  setError(null);
  
  try {
    // Cargar ambos endpoints en paralelo
    const [pagosResponse, ctaCteResponse] = await Promise.all([
      getResumenPagosSucursal(sucursalId, desde, hasta),
      getCtaCte(sucursalId, desde, hasta)
    ]);
    
    
    // Procesar respuesta de pagos (igual que antes)
    if (Array.isArray(pagosResponse)) {
      setResumen(pagosResponse);
      setData([]);
      setPage(0);
    } else if (pagosResponse.success) {
      setData(pagosResponse.data || []);
      setResumen(pagosResponse.resumen || []);
      setPage(0);
    } else {
      setError(pagosResponse.error || 'Error al cargar los datos de pagos');
    }
    
    // Procesar respuesta de cuenta corriente
    if (ctaCteResponse && typeof ctaCteResponse === 'object') {
      if (ctaCteResponse.total_cta_cte !== undefined) {
        setCtaCte(ctaCteResponse);
      } else if (Array.isArray(ctaCteResponse) && ctaCteResponse.length > 0) {
        setCtaCte(ctaCteResponse[0]);
      } else if (ctaCteResponse.success && ctaCteResponse.data) {
        setCtaCte(ctaCteResponse.data);
      }
    }
    
  } catch (error) {
    console.error('Error al cargar datos:', error);
    setError(error.message || 'Error al cargar los datos');
  } finally {
    setLoadingData(false);
  }
};

  // Manejadores de paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRows({});
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows({});
  };

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Abre el dialog de facturas asociadas a un medio de pago (la carga la maneja DialogFacturasMedioPago)
  const abrirDialogFacturas = (item) => {
    setMedioPagoSeleccionado(item);
    setDialogFacturasOpen(true);
  };

  const cerrarDialogFacturas = () => {
    setDialogFacturasOpen(false);
    setMedioPagoSeleccionado(null);
  };

const resumenConCtaCte = [...resumen];

if (ctaCte && ctaCte.total_cta_cte !== undefined && ctaCte.total_cta_cte !== null) {
  resumenConCtaCte.push({
    medio_pago: 'Cuenta Corriente',
    cantidad_pagos: ctaCte.cantidad || 1,
    total_importe: parseFloat(ctaCte.total_cta_cte),
    promedio_importe: parseFloat(ctaCte.total_cta_cte),
    minimo: parseFloat(ctaCte.total_cta_cte),
    maximo: parseFloat(ctaCte.total_cta_cte)
  });
}

  // Paginación - para el resumen (no hay data detallada)
const datosPaginadosConCtaCte = resumenConCtaCte.slice(
  page * rowsPerPage,
  page * rowsPerPage + rowsPerPage
);

const emptyRowsConCtaCte = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - resumenConCtaCte.length) : 0;

  // Loading
  if (loading || loadingData) {
    return (
      <CardExpandible title="Resumen de Pagos" icon={PaymentIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} sx={{ color: '#757575' }} />
        </Box>
      </CardExpandible>
    );
  }

  // Validaciones
  if (!sucursalId || !desde || !hasta) {
    let mensaje = '';
    if (!sucursalId) mensaje = 'Selecciona una sucursal';
    else if (!desde || !hasta) mensaje = 'Selecciona un rango de fechas';
    
    return (
      <CardExpandible title="Resumen de Pagos" icon={PaymentIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            {mensaje}
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  if (error) {
    return (
      <CardExpandible title="Resumen de Pagos" icon={PaymentIcon}>
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </CardExpandible>
    );
  }

  if (!resumenConCtaCte || resumenConCtaCte.length === 0) {
    return (
      <CardExpandible title="Resumen de Pagos" icon={PaymentIcon}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No hay pagos registrados para este período
          </Typography>
        </Box>
      </CardExpandible>
    );
  }

  return (
    <CardExpandible 
      title="Resumen de Pagos" 
      icon={PaymentIcon}
      subtitle={sucursalNombre ? `Sucursal: ${sucursalNombre} · ` : ''}
    >
      <Box>
        {/* Resumen de totales por medio de pago */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {resumenConCtaCte.map((item, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Box sx={{ 
                // p: 1.5, 
                bgcolor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e8e8e8',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getMedioPagoIcon(item.medio_pago)}
                  <Typography variant="caption" sx={{ fontWeight: 500, color: getMedioPagoColor(item.medio_pago) }}>
                    {item.medio_pago || 'Sin definir'}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '1.1rem' }}>
                  ${formatNumber(item.total_importe || item.total)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {item.cantidad_pagos || item.cantidad || 0} pagos
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Tabla de resumen detallado */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e8e8e8' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Medio Pago</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Cantidad </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Promedio</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Mínimo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Máximo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500, color: '#424242', fontSize: '0.70rem' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginadosConCtaCte.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No hay registros para mostrar
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datosPaginadosConCtaCte.map((item, index) => {
                  const globalIndex = page * rowsPerPage + index;
                  const key = `resumen-${globalIndex}`;
                  
                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell>
                          <Chip
                            icon={getMedioPagoIcon(item.medio_pago)}
                            label={item.medio_pago || 'Sin definir'}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 24,
                              bgcolor: `${getMedioPagoColor(item.medio_pago)}15`,
                              color: getMedioPagoColor(item.medio_pago),
                              borderColor: getMedioPagoColor(item.medio_pago),
                              '& .MuiChip-icon': { fontSize: 16 }
                            }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                          {item.cantidad_pagos || item.cantidad || 0}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          ${formatNumber(item.total_importe || item.total)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                          ${formatNumber(item.promedio_importe || item.promedio || 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                          ${formatNumber(item.minimo || 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem' }}>
                          ${formatNumber(item.maximo || 0)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver facturas">
                            <IconButton
                              size="small"
                              onClick={() => abrirDialogFacturas(item)}
                              sx={{ color: '#757575' }}
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              onClick={() => toggleExpandRow(key)}
                              sx={{ color: '#757575' }}
                            >
                              {expandedRows[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {/* Fila expandida con más detalles */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0 }}>
                          <Box sx={{ 
                            display: expandedRows[key] ? 'block' : 'none'
                          }}>
                            <Box sx={{ 
                              p: 1.5, 
                              bgcolor: '#fafafa',
                              borderTop: '1px solid #e8e8e8'
                            }}>
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: 0.5
                                  }}>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 500, color: '#424242', minWidth: 80 }}>Medio Pago:</span>
                                      <span>{item.medio_pago || '-'}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 500, color: '#424242', minWidth: 80 }}>Cantidad:</span>
                                      <span>{item.cantidad_pagos || item.cantidad || 0}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 500, color: '#424242', minWidth: 80 }}>Total:</span>
                                      <span style={{ fontWeight: 500 }}>${Number(item.total_importe || item.total).toLocaleString()}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 500, color: '#424242', minWidth: 80 }}>Promedio:</span>
                                      <span>${Number(item.promedio_importe || item.promedio || 0).toLocaleString()}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 500, color: '#424242', minWidth: 80 }}>Mínimo:</span>
                                      <span>${Number(item.minimo || 0).toLocaleString()}</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', gap: 0.5 }}>
                                      <span style={{ fontWeight: 500, color: '#424242', minWidth: 80 }}>Máximo:</span>
                                      <span>${Number(item.maximo || 0).toLocaleString()}</span>
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
              {emptyRowsConCtaCte > 0 && (
                <TableRow style={{ height: 53 * emptyRowsConCtaCte }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={7}
                  count={resumenConCtaCte.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} de ${count}`
                  }
                  sx={{
                    '.MuiTablePagination-select': {
                      fontSize: '0.8rem'
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* Información adicional */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            {/* {mpStoreId && `MP Store ID: ${mpStoreId} · `} */}
            Sucursal ID: {sucursalId}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total de medios de pago: {resumenConCtaCte.length} · Actualizado: {moment().format('DD-MM-YYYY HH:mm')}
          </Typography>
        </Box>
      </Box>

      <DialogFacturasMedioPago
        open={dialogFacturasOpen}
        onClose={cerrarDialogFacturas}
        medioPago={medioPagoSeleccionado}
        sucursalId={sucursalId}
        sucursalNombre={sucursalNombre}
        desde={desde}
        hasta={hasta}
      />
    </CardExpandible>
  );
};

export default CardPagos;